/**
 * RecaptchaToken hook
 *
 * Attach to a <form> that contains a hidden input named "recaptcha_token"
 * and carries the reCAPTCHA Enterprise site key in `data-sitekey`.
 *
 * Loads the reCAPTCHA Enterprise script on demand, fetches a token when the
 * form mounts and refreshes it every 90 seconds (tokens expire after two
 * minutes), and injects the freshest token into the hidden input as the form
 * is submitted — before LiveView serializes the form data. Injecting at
 * submit time also means LiveView re-renders can't wipe the token out of the
 * DOM between refreshes.
 */
const SCRIPT_ID = "recaptcha-enterprise-script";
const REFRESH_MS = 90 * 1000;

function loadScript(siteKey) {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha && window.grecaptcha.enterprise) {
      resolve();
      return;
    }

    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
      document.head.appendChild(script);
    }
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", reject);
  });
}

const RecaptchaToken = {
  mounted() {
    this.token = null;

    this.onSubmit = () => {
      const input = this.el.querySelector('input[name="recaptcha_token"]');
      if (input && this.token) input.value = this.token;
    };
    this.el.addEventListener("submit", this.onSubmit);

    this.refresh();
    this.interval = setInterval(() => this.refresh(), REFRESH_MS);
  },

  destroyed() {
    clearInterval(this.interval);
    this.el.removeEventListener("submit", this.onSubmit);
  },

  refresh() {
    const siteKey = this.el.dataset.sitekey;
    if (!siteKey) return;

    loadScript(siteKey)
      .then(
        () =>
          new Promise((resolve) => window.grecaptcha.enterprise.ready(resolve)),
      )
      .then(() =>
        window.grecaptcha.enterprise.execute(siteKey, { action: "submit" }),
      )
      .then((token) => {
        this.token = token;
      })
      .catch(() => {
        // Leave the last token in place; the server rejects stale/missing
        // tokens and the visitor can retry.
      });
  },
};

export default RecaptchaToken;
